import { defaultBillingAccountId, preRecurringBillingAccountId } from "@/mock-api/data/billingAccountIds.ts";
import { debianIcon, ubuntuIcon } from "@/mock-api/data/icons.ts";
import { mockUserId } from "@/mock-api/data/userExample.ts";
import type {
    MetalCommitment,
    MetalCommitmentOffer,
    MetalLease,
    MetalMachine,
    MetalOs,
    MetalOsType,
    MetalSpec,
    MetalStateHistory,
} from "@warrenio/api-spec/spec.oats.gen";

export interface MetalDbData {
    metalOs: MetalOs[];
    metalOsTypes: MetalOsType[];
}

export interface MetalLocationDbData {
    metalSpecs: MetalSpec[];
    metalMachines: MetalMachine[];
    metalLeases: MetalLease[];
    metalStates: MetalStateHistory[];
    metalCommitments: MetalCommitment[];
    metalCommitmentOffers: MetalCommitmentOffer[];
}

const updated_at = "2024-01-01 13:45:56";
const created_at = updated_at;

const acting_user_id = mockUserId;

export const metalSpecUuid1 = "00000000-0000-0000-0001-000000000001";
export const metalSpecUuid2 = "00000000-0000-0000-0001-000000000002";
export const metalSpecUuid3 = "00000000-0000-0000-0001-000000000003";
export const metalSpecUuid4 = "00000000-0000-0000-0001-000000000004";

export const metalLeaseUuid1 = "00000000-0000-0000-0001-000000000001";
export const metalLeaseUuid2 = "00000000-0000-0000-0001-000000000002";
export const metalLeaseUuid3 = "00000000-0000-0000-0001-000000000003";

export const metalCommitmentUuid1 = "00000000-0000-0000-0001-000000000001";
export const metalCommitmentUuid2 = "00000000-0000-0000-0001-000000000002";
export const metalCommitmentUuid3 = "00000000-0000-0000-0001-000000000003";
export const metalCommitmentUuid4 = "00000000-0000-0000-0001-000000000004";

export const metalExampleConfig = {
    metalOs: [
        {
            os_id: "debian_10",
            os_type_id: "debian",
            version_title: "10",
            updated_at,
            is_published: true,
            is_deleted: false,
        },
        {
            os_id: "debian_11",
            os_type_id: "debian",
            version_title: "11",
            updated_at,
            is_published: true,
            is_deleted: false,
        },
        {
            os_id: "ubuntu_22",
            os_type_id: "ubuntu",
            version_title: "22",
            updated_at,
            is_published: true,
            is_deleted: false,
        },
        // Not published
        {
            os_id: "slackware_1",
            os_type_id: "slackware",
            version_title: "22",
            updated_at,
            is_published: false,
            is_deleted: false,
        },
    ],
    metalOsTypes: [
        {
            os_type_id: "debian",
            title: "Debian",
            icon: debianIcon,
            updated_at,
            is_deleted: false,
        },
        {
            os_type_id: "ubuntu",
            title: "Ubuntu",
            icon: ubuntuIcon,
            updated_at,
            is_deleted: false,
        },
        // Not published
        {
            os_type_id: "slackware",
            title: "Slackware",
            icon: "",
            updated_at,
            is_deleted: false,
        },
    ],
} satisfies MetalDbData;

export const metalExampleLocationConfig = {
    metalSpecs: [
        {
            spec_id: 1,
            uuid: metalSpecUuid1,
            title: "AMD Epyc 9124",
            subtitle: "Faster than a speeding bullet",
            description: `• AMD 9124, 16 Cores @ 3 GHz
• 128 GB RAM
• 2 x 1.9TB NVME
• 1OGbps NIC`,
            is_visible: true,
            is_deleted: false,
            created_at,
            updated_at,
        },
        {
            spec_id: 2,
            uuid: metalSpecUuid2,
            title: "Intel Xeon 1234",
            subtitle: "Pay more for less",
            description: `• Intel 1234, 8 Cores @ 2 GHz
• 64 GB RAM
• 2 x 480GB NVME
• 1OGbps NIC`,
            is_visible: true,
            is_deleted: false,
            created_at,
            updated_at,
        },
        // Invisible
        {
            spec_id: 3,
            uuid: metalSpecUuid3,
            title: "Intel 386",
            subtitle: "Old but gold",
            description: "• Intel 386, 1 Core @ 0.003 GHz",
            is_visible: false,
            is_deleted: false,
            created_at,
            updated_at,
        },
        // Unavailable
        {
            spec_id: 4,
            uuid: metalSpecUuid4,
            title: "ARM Cortex",
            subtitle: "Not available",
            description: "• ARM Cortex, 4 Cores @ 1 GHz",
            is_visible: true,
            is_deleted: false,
            created_at,
            updated_at,
        },
    ],
} satisfies Partial<MetalLocationDbData>;

export const metalExampleData = {
    metalMachines: [
        // Clean
        {
            machine_id: 1,
            uuid: "00000000-0000-0000-0002-000000000001",
            label: "M1",
            spec_id: 1,
            current_state_id: 1,
            mac_addresses: ["00:00:5e:00:53:01"],
            ip_public_v4: "10.0.0.1",
            ip_public_v6: "2001:db8::1",
            ssh_credentials: "ssh user@example.com",
            admin_notes: "",
            created_at,
        },
        // Used
        {
            machine_id: 2,
            uuid: "00000000-0000-0000-0002-000000000002",
            label: "M2",
            spec_id: 2,
            current_state_id: 2,
            mac_addresses: ["00:00:5e:00:53:02"],
            ip_public_v4: "10.0.0.2",
            ip_public_v6: "2001:db8::2",
            ssh_credentials: "ssh another@example.com",
            admin_notes: "",
            created_at,
        },
        // Pending
        {
            machine_id: 3,
            uuid: "00000000-0000-0000-0002-000000000003",
            label: "Legacy1",
            spec_id: 3,
            current_state_id: 6,
            mac_addresses: ["00:00:5e:00:53:03"],
            ip_public_v4: "10.0.0.200",
            ip_public_v6: "2001:db8::2",
            ssh_credentials: "ssh another@example.com",
            admin_notes: "",
            created_at,
        },
        // Clean (spec 2)
        {
            machine_id: 4,
            uuid: "00000000-0000-0000-0002-000000000004",
            label: "M4",
            spec_id: 2,
            current_state_id: 5,
            mac_addresses: ["00:00:5e:00:53:04"],
            ip_public_v4: "10.0.0.4",
            ip_public_v6: null,
            ssh_credentials: "ssh another@example.com",
            admin_notes: "",
            created_at,
        },
        {
            machine_id: 5,
            uuid: "00000000-0000-0000-0002-000000000005",
            label: "M4",
            spec_id: 1,
            current_state_id: 3,
            mac_addresses: ["00:00:5e:00:53:04"],
            ip_public_v4: "10.0.0.4",
            ip_public_v6: null,
            ssh_credentials: "ssh another@example.com",
            admin_notes: "",
            created_at,
        },
    ],
    metalStates: [
        {
            state_id: 1,
            machine_id: 1,

            changed_at: updated_at,
            acting_user_id,
            action: "admin_create",
            billing_account_id: preRecurringBillingAccountId,
            lease_id: 1,
            os_id: "debian_11",
            status: "in_use",
        },
        {
            state_id: 2,
            machine_id: 2,

            changed_at: updated_at,
            acting_user_id,
            action: "admin_create",
            billing_account_id: preRecurringBillingAccountId,
            lease_id: 2,
            os_id: "debian_10",
            status: "in_use",
        },
        {
            state_id: 3,
            machine_id: 3,

            changed_at: updated_at,
            acting_user_id,
            action: "admin_create",
            billing_account_id: null,
            lease_id: null,
            os_id: "slackware",
            status: "available",
        },
        {
            state_id: 4,
            machine_id: 3,

            changed_at: updated_at,
            acting_user_id,
            action: "admin_update",
            billing_account_id: null,
            lease_id: null,
            os_id: "slackware",
            status: "deleted",
        },
        {
            state_id: 5,
            machine_id: 4,

            changed_at: updated_at,
            acting_user_id,
            action: "admin_create",
            billing_account_id: null,
            lease_id: null,
            os_id: "debian_11",
            status: "available",
        },
        {
            state_id: 6,
            machine_id: 3,

            changed_at: updated_at,
            acting_user_id,
            action: "admin_create",
            billing_account_id: preRecurringBillingAccountId,
            lease_id: 3,
            os_id: "slackware",
            status: "pending",
        },
    ],
    metalLeases: [
        {
            lease_id: 1,
            machine_id: 1,

            uuid: metalLeaseUuid1,
            user_id: mockUserId,
            requested_os_id: "debian_11",
            created_at,
            display_name: "My machine",
            updated_at,
        },
        {
            lease_id: 2,
            machine_id: 2,

            uuid: metalLeaseUuid2,
            user_id: mockUserId,
            requested_os_id: "debian_10",
            created_at,
            display_name: "My machine 2",
            updated_at,
        },
        {
            lease_id: 3,
            machine_id: 3,

            uuid: metalLeaseUuid3,
            user_id: mockUserId,
            requested_os_id: "slackware",
            created_at,
            display_name: "My machine 3",
            updated_at,
        },
    ],

    metalCommitments: [
        {
            uuid: metalCommitmentUuid1,
            billing_account_id: defaultBillingAccountId,
            user_id: mockUserId,
            resource_request: {
                machine_spec_uuid: metalSpecUuid1,
                display_name: "example machine",
                requested_os_id: "ubuntu_22",
                type: "new_metal_lease",
            },
            price: "2436",
            status: "requested",
            period_hours: 8760,
            starts_at: created_at,
            created_at,
            coverages: [
                {
                    acting_user_id: 28,
                    commitment_uuid: metalCommitmentUuid1,
                    created_at: "2025-12-20 13:48:11",
                    ends_at: "2026-03-20 14:15:06",
                    metal_lease_uuid: metalLeaseUuid1,
                    starts_at: "2025-12-20 14:15:06",
                    temporal_status: "past",
                },
                {
                    acting_user_id: 28,
                    commitment_uuid: metalCommitmentUuid1,
                    created_at: "2026-03-20 14:15:07",
                    ends_at: "2026-12-20 14:15:07",
                    metal_lease_uuid: metalLeaseUuid2,
                    starts_at: "2026-03-20 14:15:07",
                    temporal_status: "current",
                },
            ],
        },
        {
            uuid: metalCommitmentUuid2,
            billing_account_id: defaultBillingAccountId,
            user_id: mockUserId,
            resource_request: {
                machine_spec_uuid: metalSpecUuid1,
                display_name: "my-com-03",
                requested_os_id: "debian_11",
                type: "new_metal_lease",
            },
            price: "1300",
            status: "billed",
            period_hours: 2190,
            starts_at: created_at,
            created_at,
            coverages: [
                {
                    acting_user_id: 28,
                    commitment_uuid: metalCommitmentUuid2,
                    created_at: "2026-05-05 18:26:31",
                    ends_at: "2027-11-02 17:30:29",
                    metal_lease_uuid: metalLeaseUuid1,
                    starts_at: "2027-08-03 17:30:29",
                    temporal_status: "future",
                },
            ],
        },
        /* {
            uuid: metalCommitmentUuid3,
            billing_account_id: defaultBillingAccountId,
            user_id: mockUserId,

            price: 700,
            status: "allocated",
            period_hours: 2190,
            created_at,
        },
        {
            uuid: metalCommitmentUuid4,
            billing_account_id: defaultBillingAccountId,
            user_id: mockUserId,

            price: 250,
            status: "ended",
            period_hours: 730,
            created_at,
        }, */
    ],

    metalCommitmentOffers: [
        {
            machine_spec_uuid: metalSpecUuid1,
            period_hours: 730,
            price: "99",
        },
        {
            machine_spec_uuid: metalSpecUuid1,
            period_hours: 2190,
            price: "300",
        },
        {
            machine_spec_uuid: metalSpecUuid2,
            period_hours: 730,
            price: "300",
        },
        {
            machine_spec_uuid: metalSpecUuid2,
            period_hours: 2190,
            price: "600",
        },
        {
            machine_spec_uuid: metalSpecUuid2,
            period_hours: 8760,
            price: "999",
        },
    ],
} satisfies Partial<MetalLocationDbData>;
